import {
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { UserInfo } from "@models/user";
import { AuthService } from "@services/auth.service";
import { SearchPostalCodeService } from "@services/search-postal-code.service";
import { SnackBarService } from "@services/snack-bar.service";
import { SignupRequestBody } from "@services/types/signup-request.type";
import { cityValidator } from "@shared/validatos";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { EventLocationState } from "../../../../utils/constants/event-location-state-brazil.constant";

@Component({
  selector: "app-cart-address-section",
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    NgxMaskDirective,
    MatTooltipModule,
  ],
  providers: [provideNgxMask()],
  templateUrl: "./cart-address-section.component.html",
  styleUrl: "./cart-address-section.component.scss",
})
export class CartAddressSectionComponent implements OnInit, OnDestroy {
  readonly currentUser = input<UserInfo | undefined>(undefined);

  readonly hasAddress = computed(() => !!this.currentUser()?.address);

  @Output() continueToCheckoutClick = new EventEmitter();

  private readonly formBuilder = inject(FormBuilder);
  private destroy$ = new Subject<void>();

  private readonly searchPostalCodeService = inject(SearchPostalCodeService);
  private readonly snackbarService = inject(SnackBarService);
  private readonly authService = inject(AuthService);

  isBrazilianCostumer = false;
  loading = false;
  brazilianStates = EventLocationState;
  isLoadingCep = false;

  addressForm = this.formBuilder.group({
    country: ["", [Validators.required]],
    state: ["", [Validators.required]],
    city_name: ["", [Validators.required, cityValidator]],
    street: ["", [Validators.required]],
    number: ["", [Validators.required]],
    district: ["", [Validators.required]],
    postal_code: ["", [Validators.required]],
    additional_information: [""],
  });

  ngOnInit(): void {
    this.subscribeToCountryInput();
    this.verifyCurrentUserAddress();
    this.subscribeToCepInput();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  saveAddressClick() {
    if (this.addressForm.invalid || !this.currentUser) {
      this.snackbarService.openSnackBar("Dados inválidos", "Fechar", "error");
      return;
    }
    this.loading = true;

    const {
      state,
      street,
      number,
      district,
      postal_code,
      country,
      additional_information,
      city_name,
    } = this.addressForm.value;

    const updateUserBody: SignupRequestBody = {
      user: {
        ...this.currentUser(),
        country,
        address_attributes: {
          country,
          state,
          street,
          number,
          district,
          city_name,
          postal_code,
          additional_information,
        },
      },
    } as SignupRequestBody;

    this.authService
      .updateUserInfo(updateUserBody)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(() => {
        this.continueToCheckoutClick.emit();
      });
  }

  continueWithoutChangesClick() {
    this.continueToCheckoutClick.emit();
  }

  private verifyCurrentUserAddress() {
    const userCountry = this.currentUser()?.address?.country ?? "BR";
    this.addressForm.patchValue({
      ...this.currentUser()?.address,
      country: userCountry,
    });
  }

  private subscribeToCepInput() {
    const { postal_code } = this.addressForm.controls;

    postal_code.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500),
        map((cepValue) => this.sanitizeCep(cepValue)),
        distinctUntilChanged(),
        filter(this.verifyCep),
        tap(() => {
          this.isLoadingCep = true;
        }),
        switchMap((cep) =>
          this.searchPostalCodeService.getPostalCode(cep).pipe(
            finalize(() => {
              this.isLoadingCep = false;
            }),
          ),
        ),
      )
      .subscribe((cepResponse) => {
        if (!cepResponse) {
          return;
        }
        this.addressForm.patchValue(
          {
            street: cepResponse.logradouro,
            district: cepResponse.bairro,
            city_name: cepResponse.localidade,
            state: cepResponse.uf,
            additional_information: cepResponse.complemento,
          },
          {
            emitEvent: false,
          },
        );
      });
  }

  private subscribeToCountryInput() {
    const { state, country } = this.addressForm.controls;

    country.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(this.addressForm.get("country")?.value),
      )
      .subscribe((country) => {
        this.isBrazilianCostumer = country === "BR";
        if (this.isBrazilianCostumer) {
          state.setValidators(Validators.required);
        } else {
          state.addValidators(Validators.maxLength(6));
        }
        state.updateValueAndValidity({ emitEvent: false });
        this.addressForm.updateValueAndValidity();
      });
  }

  private sanitizeCep(value: unknown): string {
    return String(value ?? "").replace(/\D/g, "");
  }

  private verifyCep = (value: unknown): value is string => {
    if (!this.isBrazilianCostumer) {
      return false;
    }
    const cep = this.sanitizeCep(value);
    return cep.length === 8 && !/^0+$/.test(cep);
  };
}
